import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useTimer } from "@/lib/TimerContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import logger from "@/lib/logger";
import { trackEvent } from "@/lib/analytics";
import { Plus, X, ArrowRight, Play, Loader2 } from "lucide-react";

// Onboarding — 3-step first-run capture flow.
// Step 1: Capture up to 3 tasks.
// Step 2: Pick one to focus on first.
// Step 3: Start the existing focus timer on it (default 20 min, shorten-only).
// Skippable at every step, no confirm modal, no guilt copy.

const MAX_TASKS = 3;
const DURATION_OPTIONS = [5, 10, 15, 20];
const DEFAULT_DURATION = 20;
const TOTAL_STEPS = 3;

export default function OnboardingFlow() {
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [tasks, setTasks] = useState([]);       // [{ tempId, title, id? }] — id set after API create
  const [draft, setDraft] = useState("");
  const [pickedIdx, setPickedIdx] = useState(null);
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [submitting, setSubmitting] = useState(false);
  // Skipped-but-pending: user dismissed without completing — surface small
  // "Finish setup" link so they have a low-friction path back.
  const [skippedPending, setSkippedPending] = useState(false);

  const inputRef = useRef(null);
  const startedAtRef = useRef(null); // ms timestamp of first modal-open, for duration_ms
  const startedFiredRef = useRef(false); // guard against duplicate onboarding_started
  const { user } = useAuth();
  const navigate = useNavigate();
  const { linkTask, setPreset, switchMode, start } = useTimer();

  // Trigger detection: new user with no tasks & no projects, no dismissal flag.
  // Existing users have data → never trigger. Users who already completed or
  // dismissed → never re-trigger (until they use the Finish-setup link).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const prefs = await api.getPreferences();
        if (cancelled) return;
        if (prefs.onboarding_completed_at) {
          setLoading(false);
          return;
        }
        if (prefs.onboarding_dismissed_at) {
          // Only surface the Finish-setup link if the user still hasn't
          // done anything productive. Grandfathered users (dismissed a
          // previous onboarding but have tasks/projects) don't need
          // another nudge — they're already using the app.
          const [existingTasks, existingProjects] = await Promise.all([
            api.getTasks({ archived: false }).catch(() => []),
            api.getProjects().catch(() => []),
          ]);
          if (!cancelled && existingTasks.length === 0 && existingProjects.length === 0) {
            setSkippedPending(true);
          }
          setLoading(false);
          return;
        }
        const [existingTasks, existingProjects] = await Promise.all([
          api.getTasks({ archived: false }).catch(() => []),
          api.getProjects().catch(() => []),
        ]);
        if (cancelled) return;
        if (existingTasks.length === 0 && existingProjects.length === 0) {
          setActive(true);
          if (!startedFiredRef.current) {
            startedAtRef.current = Date.now();
            startedFiredRef.current = true;
            try { trackEvent("onboarding_started"); } catch { /* analytics best-effort */ }
          }
        }
      } catch (e) {
        logger.debug("onboarding:trigger-check-failed", e);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  // "Finish setup" link → dispatch → this listener re-opens the flow.
  useEffect(() => {
    const handler = () => {
      setStep(1);
      setTasks([]);
      setDraft("");
      setPickedIdx(null);
      setDuration(DEFAULT_DURATION);
      setSkippedPending(false);
      setActive(true);
      startedAtRef.current = Date.now();
      startedFiredRef.current = true;
      try { trackEvent("onboarding_started"); } catch { /* analytics best-effort */ }
      setTimeout(() => inputRef.current?.focus(), 100);
    };
    window.addEventListener("mmntm:replay-onboarding", handler);
    return () => window.removeEventListener("mmntm:replay-onboarding", handler);
  }, []);

  // Autofocus the first-step input when the modal opens.
  useEffect(() => {
    if (active && step === 1) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [active, step]);

  const addTask = useCallback(() => {
    const title = draft.trim();
    if (!title || tasks.length >= MAX_TASKS) return;
    const nextIndex = tasks.length + 1;
    setTasks((prev) => [...prev, { tempId: `${Date.now()}-${prev.length}`, title }]);
    setDraft("");
    try { trackEvent("onboarding_task_added", { index: nextIndex }); } catch { /* analytics best-effort */ }
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [draft, tasks.length]);

  const removeTask = useCallback((tempId) => {
    setTasks((prev) => prev.filter((t) => t.tempId !== tempId));
  }, []);

  // Persist all captured tasks via the normal task-creation API so they
  // land as ordinary tasks in the user's list. Fills in `id` on the local
  // copy so downstream steps can link to the created task.
  const persistTasks = useCallback(async () => {
    const created = [];
    for (const t of tasks) {
      if (t.id) { created.push(t); continue; }
      try {
        const res = await api.createTask({ title: t.title, category: "daily" });
        trackEvent("task_created", { task_id: res?.id, has_project: !!res?.project_id, source: "manual" });
        created.push({ ...t, id: res?.id });
      } catch (e) {
        logger.error("onboarding:createTask-failed", e);
        created.push(t); // keep the local record so we don't lose the title on retry
      }
    }
    setTasks(created);
    return created;
  }, [tasks]);

  const finishAsCompleted = useCallback(async (createdTasks, pickedTaskId) => {
    const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : null;
    try {
      await api.updatePreferences({ onboarding_completed_at: new Date().toISOString() });
    } catch (e) {
      logger.error("onboarding:complete-flag-failed", e);
    }
    try {
      trackEvent("onboarding_completed", {
        duration_ms: durationMs,
        tasks_added: createdTasks.length,
      });
    } catch { /* analytics best-effort */ }
    // Reuse the existing timer as-is: focus mode, chosen duration, linked
    // to the picked task, then start.
    try {
      if (pickedTaskId) {
        switchMode("focus");
        setPreset(duration);
        linkTask(pickedTaskId);
        try { trackEvent("onboarding_timer_started", { duration_planned: duration }); } catch { /* analytics best-effort */ }
        // Let state settle for one microtask before starting so the linked
        // task_id makes it into the createFocusSession call.
        setTimeout(() => { start(); }, 30);
      }
    } catch (e) {
      logger.error("onboarding:start-timer-failed", e);
    }
    setActive(false);
    setSkippedPending(false);
    if (window.location.pathname !== "/") navigate("/");
    toast.success(pickedTaskId ? "Focus session started — let's go." : "You're all set.");
  }, [duration, switchMode, setPreset, linkTask, start, navigate]);

  // Skip: never confirms, never guilt-copies. Any tasks the user already
  // captured are still persisted (never throw the work away). Sets the
  // dismissed flag so the modal doesn't reappear — but exposes the small
  // Finish-setup link so they can come back.
  const handleSkip = useCallback(async () => {
    setSubmitting(true);
    try {
      if (tasks.length > 0) await persistTasks();
    } catch { /* persistTasks already logs */ }
    try {
      await api.updatePreferences({ onboarding_dismissed_at: new Date().toISOString() });
    } catch (e) {
      logger.error("onboarding:skip-flag-failed", e);
    }
    try {
      trackEvent("onboarding_skipped", { step });
    } catch { /* analytics best-effort */ }
    setActive(false);
    setSkippedPending(true);
    setSubmitting(false);
  }, [tasks, persistTasks, step]);

  const goToPick = useCallback(async () => {
    if (tasks.length === 0) return;
    setSubmitting(true);
    const created = await persistTasks();
    setSubmitting(false);
    // If nothing actually persisted (all failed), still let the user pick
    // from local titles — the timer will just start without a linked task.
    if (created.every((t) => !t.id)) {
      toast.error("Couldn't save your tasks, but you can still pick one to focus on.");
    }
    setStep(2);
  }, [tasks.length, persistTasks]);

  const pickTask = useCallback((idx) => {
    setPickedIdx(idx);
    try { trackEvent("onboarding_task_picked"); } catch { /* analytics best-effort */ }
    setStep(3);
  }, []);

  const startFocus = useCallback(async () => {
    const picked = pickedIdx != null ? tasks[pickedIdx] : null;
    await finishAsCompleted(tasks, picked?.id ?? null);
  }, [pickedIdx, tasks, finishAsCompleted]);

  if (loading || !user) return null;

  // Skipped-and-pending: render the small Finish-setup link only. No modal,
  // no popup, no badge — just a plain text link fixed bottom-right.
  if (!active) {
    if (!skippedPending) return null;
    return (
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent("mmntm:replay-onboarding"))}
        className="fixed bottom-3 right-3 z-[1000] text-[11px] px-2.5 py-1 rounded-md text-white/45 hover:text-white/70 transition-colors"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="onboarding-finish-setup-link"
        aria-label="Finish setup"
      >
        Finish setup
      </button>
    );
  }

  const progressPct = (step / TOTAL_STEPS) * 100;
  const picked = pickedIdx != null ? tasks[pickedIdx] : null;

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center" data-testid="onboarding-overlay">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-md mx-4 rounded-2xl shadow-2xl overflow-hidden mmntm-popup border"
        style={{ background: "var(--tm-bg-card)", borderColor: "var(--tm-accent)" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="onboarding-title"
      >
        {/* Step indicator */}
        <div className="h-1 w-full" style={{ background: "rgba(255,255,255,0.05)" }}>
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${progressPct}%`, background: "#A855F7" }}
            data-testid="onboarding-progress"
          />
        </div>

        <div className="flex items-center justify-between px-5 pt-4">
          <span className="text-[10px] text-white/40" data-testid="onboarding-step-indicator">
            Step {step} of {TOTAL_STEPS}
          </span>
          <button
            type="button"
            onClick={handleSkip}
            disabled={submitting}
            className="text-[11px] text-white/40 hover:text-white/60 transition-colors disabled:opacity-50"
            data-testid="onboarding-skip"
          >
            Skip for now
          </button>
        </div>

        <div className="px-5 py-6">
          {/* --- Step 1: Capture up to 3 tasks --- */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center">
                <h2 id="onboarding-title" className="text-lg font-bold text-white mb-1"
                    data-testid="onboarding-title-capture">
                  What&rsquo;s on your plate?
                </h2>
                <p className="text-sm text-white/50">Add up to {MAX_TASKS} things. You can shorten this later.</p>
              </div>

              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
                  disabled={tasks.length >= MAX_TASKS}
                  maxLength={140}
                  className="flex-1 px-3 py-2 text-sm rounded-lg bg-white/[0.04] text-white outline-none placeholder:text-white/25 disabled:opacity-50"
                  style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                  placeholder={tasks.length >= MAX_TASKS ? `Max ${MAX_TASKS} — remove one to add more` : "Type a task and press Enter"}
                  data-testid="onboarding-task-input"
                  aria-label="Task title"
                />
                <button
                  type="button"
                  onClick={addTask}
                  disabled={!draft.trim() || tasks.length >= MAX_TASKS}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40"
                  style={{ background: "#A855F7" }}
                  data-testid="onboarding-add-task-btn"
                  aria-label="Add task"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <ul className="space-y-2 min-h-[52px]" data-testid="onboarding-task-list">
                {tasks.map((t, i) => (
                  <li
                    key={t.tempId}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-white/85"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                    data-testid={`onboarding-task-chip-${i}`}
                  >
                    <span className="truncate">{t.title}</span>
                    <button
                      type="button"
                      onClick={() => removeTask(t.tempId)}
                      className="text-white/35 hover:text-white/70 shrink-0"
                      aria-label={`Remove ${t.title}`}
                      data-testid={`onboarding-task-remove-${i}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
                {tasks.length === 0 && (
                  <li className="text-xs text-white/25 italic px-1">No tasks yet — type one above.</li>
                )}
              </ul>

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={goToPick}
                  disabled={tasks.length === 0 || submitting}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40"
                  style={{ background: "#A855F7" }}
                  data-testid="onboarding-next-btn"
                >
                  {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                  Next
                </button>
              </div>
            </div>
          )}

          {/* --- Step 2: Pick one --- */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center">
                <h2 id="onboarding-title" className="text-lg font-bold text-white mb-1"
                    data-testid="onboarding-title-pick">
                  Which one first?
                </h2>
                <p className="text-sm text-white/50">Tap the one you want to start on.</p>
              </div>

              <div className="space-y-2" role="radiogroup" aria-label="Pick a task">
                {tasks.map((t, i) => (
                  <button
                    key={t.tempId}
                    type="button"
                    onClick={() => pickTask(i)}
                    className="w-full text-left px-4 py-3.5 rounded-xl text-sm font-medium text-white transition-all hover:opacity-95"
                    style={{
                      background: "rgba(168,85,247,0.10)",
                      border: "1px solid rgba(168,85,247,0.35)",
                    }}
                    data-testid={`onboarding-pick-task-${i}`}
                    role="radio"
                    aria-checked={pickedIdx === i}
                  >
                    {t.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* --- Step 3: Start the timer --- */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="text-center">
                <h2 id="onboarding-title" className="text-lg font-bold text-white mb-1"
                    data-testid="onboarding-title-start">
                  Ready to focus
                </h2>
                <p className="text-sm text-white/60">
                  Starting a focus session on{" "}
                  <span className="text-white font-semibold" data-testid="onboarding-picked-title">
                    {picked?.title || "your task"}
                  </span>
                </p>
              </div>

              <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Focus duration in minutes">
                {DURATION_OPTIONS.map((min) => {
                  const isActive = duration === min;
                  return (
                    <button
                      key={min}
                      type="button"
                      onClick={() => setDuration(min)}
                      role="radio"
                      aria-checked={isActive}
                      className="flex flex-col items-center gap-0.5 px-2 py-2.5 rounded-lg text-xs font-semibold transition-all"
                      style={{
                        background: isActive ? "rgba(168,85,247,0.18)" : "rgba(255,255,255,0.04)",
                        border: isActive ? "1px solid rgba(168,85,247,0.6)" : "1px solid rgba(255,255,255,0.08)",
                        color: isActive ? "#fff" : "rgba(255,255,255,0.55)",
                      }}
                      data-testid={`onboarding-duration-${min}`}
                    >
                      <span className="text-[13px] tabular-nums">{min}</span>
                      <span className="text-[9px] opacity-70 leading-none">min</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-white/35 text-center leading-snug">
                20 min max for your first session — shorter is fine, longer is a choice for later.
              </p>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={startFocus}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ background: "#A855F7" }}
                  data-testid="onboarding-start-focus-btn"
                >
                  <Play className="h-4 w-4" />
                  Start focus session
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
